// poiesis/src/views/chunks.ts
// Owner 2026-10-30: "There should be no limit the FE just lazily loaded them."
//
// A time chart asks for the window ON SCREEN at the chosen grain, in chunks of
// at most the served point budget (`points_per_request`). Chunks are laid on
// FIXED bucket boundaries of the tenant's calendar (whole local days for hours
// and days, Mondays for weeks, first-of-month for months), counted from one
// anchor — so a chunk is the same question whatever the zoom or pan that needed
// it, and the query cache answers it the second time. A boundary never splits
// a bucket, so the chunks' rows simply follow each other: nothing here sums,
// folds or re-buckets (the engine buckets; the client concatenates).
import type { ReduceRequest, ReducedFrame } from './contract'
import { todayInZone, zonedMidnightUtc } from '../locale'
import { GRAIN_MS } from './bucket'

export interface TimeWindow { period_start: string; period_end: string }

const DAY_MS = 864e5
/** The anchor every chunk counts from: Monday 3 January 2000 (local). */
const ANCHOR = { y: 2000, m: 0, d: 3 }
const ANCHOR_DAY = Date.UTC(ANCHOR.y, ANCHOR.m, ANCHOR.d)

/** The local calendar day number (since the anchor) an instant falls on. */
function dayNumber(ms: number, tz: string): number {
  const { y, m, d } = todayInZone(tz, new Date(ms))
  return Math.round((Date.UTC(y, m, d) - ANCHOR_DAY) / DAY_MS)
}
function monthNumber(ms: number, tz: string): number {
  const { y, m } = todayInZone(tz, new Date(ms))
  return (y - ANCHOR.y) * 12 + m
}

/** How one chunk is laid: `days` whole local days, or `months` whole months. */
function chunkSpan(grain: string, budget: number): { days: number } | { months: number } | null {
  switch (grain) {
    case 'hour': return { days: Math.max(1, Math.floor(budget / 24)) }
    case 'day': return { days: Math.max(1, budget) }
    case 'week': return { days: 7 * Math.max(1, budget) }
    case 'month': return { months: Math.max(1, budget) }
    default: return null // a grain with no calendar chunking reads in one question
  }
}

/** The chunks covering `visible` (clamped to `bounds`, the page's window) at
 *  `grain`, each at most `budget` buckets, chronological. Each chunk is
 *  clamped to `bounds` too — the page never asks outside its own period. */
export function chunkWindows(visible: TimeWindow, bounds: TimeWindow, grain: string, budget: number,
  tz: string): TimeWindow[] {
  const lo = Math.max(Date.parse(visible.period_start), Date.parse(bounds.period_start))
  const hi = Math.min(Date.parse(visible.period_end), Date.parse(bounds.period_end))
  const bLo = Date.parse(bounds.period_start), bHi = Date.parse(bounds.period_end)
  if (!(hi > lo)) return []
  const span = chunkSpan(grain, budget)
  if (!span) return [{ period_start: new Date(lo).toISOString(), period_end: new Date(hi).toISOString() }]
  const edge = (i: number) => 'days' in span
    ? zonedMidnightUtc(tz, ANCHOR.y, ANCHOR.m, ANCHOR.d + i * span.days).getTime()
    : zonedMidnightUtc(tz, ANCHOR.y, ANCHOR.m + i * span.months, 1).getTime()
  const indexOf = (ms: number) => 'days' in span
    ? Math.floor(dayNumber(ms, tz) / span.days)
    : Math.floor(monthNumber(ms, tz) / span.months)
  const out: TimeWindow[] = []
  for (let i = indexOf(lo); i <= indexOf(hi - 1); i++) {
    const s = Math.max(edge(i), bLo), e = Math.min(edge(i + 1), bHi)
    if (e > s) out.push({ period_start: new Date(s).toISOString(), period_end: new Date(e).toISOString() })
  }
  return out
}

/** The request for one chunk: the same question over the chunk's window. */
export function chunkRequest(request: ReduceRequest, w: TimeWindow): ReduceRequest {
  return { ...request, scope: { ...request.scope, period_start: w.period_start, period_end: w.period_end } }
}

/** The loaded chunks as ONE frame: their rows in chunk order (chronological,
 *  no bucket split across two chunks), the first frame's meta with every
 *  chunk's warnings and drops (each said once). */
export function mergeChunkFrames(frames: ReducedFrame[]): ReducedFrame | undefined {
  if (frames.length === 0) return undefined
  if (frames.length === 1) return frames[0]
  const once = <T,>(xs: T[]) => [...new Map(xs.map((x) => [JSON.stringify(x), x])).values()]
  const first = frames[0]!
  return {
    rows: frames.flatMap((f) => f.rows),
    meta: {
      ...first.meta,
      warnings: once(frames.flatMap((f) => f.meta.warnings ?? [])),
      dropped: once(frames.flatMap((f) => f.meta.dropped ?? [])),
    },
  } as ReducedFrame
}

/** The rows of a time-bucket frame whose bucket overlaps `visible` — a chunk
 *  reaches past the visible window; the chart draws only what is visible. */
export function visibleRows(frame: ReducedFrame, visible: TimeWindow, grain: string): ReducedFrame {
  if (frame.meta.dimensions[0] !== 'bucket_start') return frame
  const lo = Date.parse(visible.period_start), hi = Date.parse(visible.period_end)
  const len = GRAIN_MS[grain] ?? 0
  const rows = frame.rows.filter((r) => {
    const t = Date.parse(String(r.bucket_start))
    return !Number.isFinite(t) || (t < hi && t + len > lo)
  })
  return rows.length === frame.rows.length ? frame : { ...frame, rows }
}
